import type { Hash } from "viem";

/** Deployment phase identifier. */
export type DeploymentPhase =
  | "idle"
  | "foundation"
  | "v2"
  | "v3"
  | "v4"
  | "universal-router"
  | "uniswapx"
  | "erc8004"
  | "vault-core"
  | "seed-tokens"
  | "seed-liquidity"
  | "seed-swaps"
  | "seed-agents"
  | "seed-vaults"
  | "seed-deposits"
  | "discovering-contracts"
  | "funding-accounts"
  | "funding-tokens"
  | "deploying-v4-helpers"
  | "seeding-v4-pools"
  | "deploying-erc8004"
  | "deploying-vault-core"
  | "complete"
  | "error";

/** Phase metadata for display. */
export interface PhaseInfo {
  id: DeploymentPhase;
  name: string;
  index: number;
  /** Lucide icon name for the overlay. */
  icon: string;
  /** Expected contract deployments in this phase. */
  expectedContracts: number;
}

/** Per-phase timing/gas/tx detail. */
export interface PhaseDetail {
  startMs: number;
  endMs?: number;
  durationMs?: number;
  contractsDeployed: number;
  txHashes: Hash[];
  gasUsed: bigint;
}

/** Ordered deployment phases for from-scratch mode. */
export const DEPLOY_PHASES: readonly PhaseInfo[] = [
  {
    id: "foundation",
    name: "Foundation (WETH9, Permit2)",
    index: 0,
    icon: "Box",
    expectedContracts: 2,
  },
  {
    id: "v2",
    name: "Uniswap V2",
    index: 1,
    icon: "GitBranch",
    expectedContracts: 3,
  },
  {
    id: "v3",
    name: "Uniswap V3",
    index: 2,
    icon: "Layers",
    expectedContracts: 11,
  },
  {
    id: "v4",
    name: "Uniswap V4",
    index: 3,
    icon: "Hexagon",
    expectedContracts: 7,
  },
  {
    id: "universal-router",
    name: "Universal Router",
    index: 4,
    icon: "Route",
    expectedContracts: 1,
  },
  {
    id: "uniswapx",
    name: "UniswapX",
    index: 5,
    icon: "Zap",
    expectedContracts: 4,
  },
  {
    id: "erc8004",
    name: "ERC-8004 Identity",
    index: 6,
    icon: "Fingerprint",
    expectedContracts: 2,
  },
  {
    id: "vault-core",
    name: "Vault Core",
    index: 7,
    icon: "Vault",
    expectedContracts: 4,
  },
  {
    id: "seed-tokens",
    name: "Seed Tokens",
    index: 8,
    icon: "Coins",
    expectedContracts: 8,
  },
  {
    id: "seed-liquidity",
    name: "Seed Liquidity",
    index: 9,
    icon: "Droplets",
    expectedContracts: 0,
  },
  {
    id: "seed-swaps",
    name: "Seed Swaps",
    index: 10,
    icon: "ArrowLeftRight",
    expectedContracts: 0,
  },
  {
    id: "seed-agents",
    name: "Seed Agents",
    index: 11,
    icon: "Bot",
    expectedContracts: 0,
  },
  {
    id: "seed-vaults",
    name: "Seed Vaults",
    index: 12,
    icon: "Building",
    expectedContracts: 3,
  },
  {
    id: "seed-deposits",
    name: "Seed Deposits",
    index: 13,
    icon: "ArrowDownToLine",
    expectedContracts: 0,
  },
] as const;

/** Ordered fork phases. */
export const FORK_PHASES: readonly PhaseInfo[] = [
  {
    id: "discovering-contracts",
    name: "Discovering Contracts",
    index: 0,
    icon: "Search",
    expectedContracts: 0,
  },
  {
    id: "funding-accounts",
    name: "Funding Accounts",
    index: 1,
    icon: "Wallet",
    expectedContracts: 0,
  },
  {
    id: "funding-tokens",
    name: "Funding Tokens",
    index: 2,
    icon: "Coins",
    expectedContracts: 0,
  },
  {
    id: "deploying-v4-helpers",
    name: "Deploying V4 Helpers",
    index: 3,
    icon: "Code",
    expectedContracts: 2,
  },
  {
    id: "seeding-v4-pools",
    name: "Seeding V4 Pools",
    index: 4,
    icon: "Droplets",
    expectedContracts: 0,
  },
  {
    id: "deploying-erc8004",
    name: "ERC-8004 Identity",
    index: 5,
    icon: "Fingerprint",
    expectedContracts: 2,
  },
  {
    id: "deploying-vault-core",
    name: "Vault Core",
    index: 6,
    icon: "Vault",
    expectedContracts: 4,
  },
] as const;

/** Seed step flags for selective seeding. */
export interface SeedSteps {
  tokens?: boolean;
  liquidity?: boolean;
  swaps?: boolean;
  agents?: boolean;
  vaults?: boolean;
  deposits?: boolean;
}

/** All protocol phases (always included in from-scratch mode). */
export const PROTOCOL_PHASES: readonly PhaseInfo[] = DEPLOY_PHASES.slice(0, 8);

/** Build seed phase lookup by iterating instead of direct indexing. */
function buildSeedPhaseMap(): Record<string, PhaseInfo> {
  const seedIds: Record<string, string> = {
    "seed-tokens": "tokens",
    "seed-liquidity": "liquidity",
    "seed-swaps": "swaps",
    "seed-agents": "agents",
    "seed-vaults": "vaults",
    "seed-deposits": "deposits",
  };
  const map: Record<string, PhaseInfo> = {};
  for (const phase of DEPLOY_PHASES) {
    const key = seedIds[phase.id];
    if (key) map[key] = phase;
  }
  return map;
}

const SEED_PHASE_MAP = buildSeedPhaseMap();

/**
 * Build a dynamic phase list based on which seed steps are enabled.
 * Protocol phases (0-7) are always included. Seed phases are filtered
 * and re-indexed to produce correct progress percentages.
 */
export function buildDeployPhases(seedSteps?: SeedSteps): PhaseInfo[] {
  const phases: PhaseInfo[] = [...PROTOCOL_PHASES];

  if (!seedSteps) {
    // All seeds enabled (default)
    for (const phase of DEPLOY_PHASES.slice(8)) {
      phases.push({ ...phase, index: phases.length });
    }
    return phases;
  }

  const stepOrder = [
    "tokens",
    "liquidity",
    "swaps",
    "agents",
    "vaults",
    "deposits",
  ] as const;
  for (const step of stepOrder) {
    if (seedSteps[step]) {
      const def = SEED_PHASE_MAP[step];
      if (def) phases.push({ ...def, index: phases.length });
    }
  }

  return phases;
}

/** Total number of contracts deployed in from-scratch mode. */
export const TOTAL_CONTRACTS = 45;

/** Deployment progress state. */
export interface DeploymentProgress {
  phase: DeploymentPhase;
  phaseName: string;
  phaseIndex: number;
  totalPhases: number;
  contractsDeployed: number;
  totalContracts: number;
  elapsedMs: number;
  error?: string;
  /** Per-phase rich detail (timing, gas, txs). */
  phaseDetails: Record<string, PhaseDetail>;
  /** Running total gas across all phases. */
  totalGasUsed: bigint;
  /** Timestamp when deployment started. */
  startedAt: number;
  /** Current per-transaction action description (during seed phases). */
  currentAction?: string;
}

/** Create initial progress state. */
export function initialProgress(
  isFork: boolean,
  phaseCount?: number,
): DeploymentProgress {
  const defaultPhases = isFork ? FORK_PHASES : DEPLOY_PHASES;
  return {
    phase: "idle",
    phaseName: "Waiting",
    phaseIndex: 0,
    totalPhases: phaseCount ?? defaultPhases.length,
    contractsDeployed: 0,
    totalContracts: isFork ? 0 : TOTAL_CONTRACTS,
    elapsedMs: 0,
    phaseDetails: {},
    totalGasUsed: 0n,
    startedAt: 0,
  };
}

/** Update progress to a new phase (clears currentAction). */
export function advanceProgress(
  prev: DeploymentProgress,
  phase: DeploymentPhase,
  phaseName: string,
  phaseIndex: number,
  contractsDeployed?: number,
): DeploymentProgress {
  return {
    ...prev,
    phase,
    phaseName,
    phaseIndex,
    contractsDeployed: contractsDeployed ?? prev.contractsDeployed,
    currentAction: undefined,
  };
}

/** Update the current seed action text. */
export function updateAction(
  prev: DeploymentProgress,
  action: string,
): DeploymentProgress {
  return { ...prev, currentAction: action };
}

/** Record that a phase has started. */
export function phaseStarted(
  prev: DeploymentProgress,
  phaseId: string,
): DeploymentProgress {
  return {
    ...prev,
    phaseDetails: {
      ...prev.phaseDetails,
      [phaseId]: {
        startMs: Date.now(),
        contractsDeployed: 0,
        txHashes: [],
        gasUsed: 0n,
      },
    },
  };
}

/** Record that a phase has completed with result data. */
export function phaseCompleted(
  prev: DeploymentProgress,
  phaseId: string,
  result: {
    contractsDeployed: number;
    gasUsed: bigint;
    txHashes: Hash[];
    durationMs: number;
  },
): DeploymentProgress {
  const existing = prev.phaseDetails[phaseId];
  const newDetail: PhaseDetail = {
    startMs: existing?.startMs ?? Date.now() - result.durationMs,
    endMs: Date.now(),
    durationMs: result.durationMs,
    contractsDeployed: result.contractsDeployed,
    txHashes: result.txHashes,
    gasUsed: result.gasUsed,
  };
  const newTotalGas = prev.totalGasUsed + result.gasUsed;
  const newContractsDeployed =
    prev.contractsDeployed + result.contractsDeployed;

  return {
    ...prev,
    phaseDetails: { ...prev.phaseDetails, [phaseId]: newDetail },
    totalGasUsed: newTotalGas,
    contractsDeployed: newContractsDeployed,
  };
}

/** Mark progress as complete (clears currentAction). */
export function completeProgress(
  prev: DeploymentProgress,
  elapsedMs: number,
): DeploymentProgress {
  return {
    ...prev,
    phase: "complete",
    phaseName: "Complete",
    phaseIndex: prev.totalPhases,
    contractsDeployed: prev.totalContracts,
    elapsedMs,
    currentAction: undefined,
  };
}

/** Mark progress as errored. */
export function errorProgress(
  prev: DeploymentProgress,
  error: string,
  elapsedMs: number,
): DeploymentProgress {
  return {
    ...prev,
    phase: "error",
    phaseName: "Error",
    error,
    elapsedMs,
  };
}
